import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import type { AppRole, Profile } from '@/types/domain';
import { authService } from './authService';
import { checkSessionStillActive } from './singleSession';

interface AuthState {
  initialised: boolean;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  /** UI hint only. Every protected action is authorised server-side (RLS / RPC / Edge Functions). */
  roles: AppRole[];
  hasRole: (role: AppRole) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  recoveringPassword: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [initialised, setInitialised] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [recoveringPassword, setRecovering] = useState(false);

  const loadUserData = useCallback(async (s: Session | null) => {
    if (!s) {
      setProfile(null);
      setRoles([]);
      return;
    }
    try {
      const [p, r] = await Promise.all([authService.fetchProfile(s.user.id), authService.fetchRoles()]);
      setProfile(p);
      setRoles(r);
    } catch {
      setRoles(['learner']);
    }
  }, []);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      await loadUserData(data.session);
      if (active) setInitialised(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === 'PASSWORD_RECOVERY') setRecovering(true);
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        // Defer: supabase-js recommends not awaiting other calls inside the callback.
        setTimeout(() => void loadUserData(s), 0);
      }
      if (event === 'SIGNED_OUT') queryClient.clear();
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadUserData, queryClient]);

  // Only one device may be signed in. Check now and then (and when the app comes
  // back into view) that a sign-in elsewhere hasn't ended this session.
  const signedIn = !!session;
  useEffect(() => {
    if (!signedIn) return;
    const check = () => void checkSessionStillActive();
    const onVisible = () => document.visibilityState === 'visible' && check();
    const timer = window.setInterval(check, 60_000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn]);

  const value = useMemo<AuthState>(
    () => ({
      initialised,
      session,
      user: session?.user ?? null,
      profile,
      roles,
      hasRole: (role) => roles.includes(role),
      refresh: () => loadUserData(session),
      signOut: async () => {
        await authService.signOut();
        setRecovering(false);
      },
      recoveringPassword,
    }),
    [initialised, session, profile, roles, loadUserData, recoveringPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
